/**
 * The column-aware step of reopening a filter from its SQL: the parser
 * (`predicateToFilterCondition`) sees only text, and several chip conditions
 * write the SAME text, so it reads them back as the plainest one:
 *
 *  - a boolean switch on SQL Server / Oracle / SQLite writes `= 1` / `= 0` —
 *    it reads back as the number, and the switch showed "Not set";
 *  - a single enum pick writes `= 'open'`, a real-FK lookup `= 42`, a loose-FK
 *    lookup `= 42` — all read back as `=`;
 *  - an enum multi-pick writes `IN ('open', 'closed')` (a lookup enum's
 *    numeric keys `IN (1, 3)`) — read back as the "in list" operator;
 *  - a JSON enum / lookup pick on a path writes the same SQL as `path_eq` /
 *    `any_eq` / `all_eq` (or, for a loose FK with a `sourcePath`, as the
 *    top-level `lookup`).
 *
 * `upgradeReopenedCondition` turns the parsed condition into the one Table
 * Explorer's chip shows for that column — the column's own default operator
 * (`defaultDraftConditionFields`: `enum` for a native / declared enum, `lookup`
 * for a real FK), a loose-FK lookup when exactly one declaration covers the
 * column (several without a path are ambiguous and stay `=`), the declared
 * JSON enum / loose FK for the path — and only when re-emitting it writes the
 * same SQL byte for byte. Otherwise the parsed condition is returned unchanged.
 */

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { FilterCondition, JsonSubOperator, TableExplorerColumn } from '../../core/types/workspaceTypes';
import { buildConditionSql } from './filterWhereBuilder';
import { categorizeDataType, defaultDraftConditionFields, type JsonOperatorContext } from './filterUtils';
import { findLooseRelationshipsForColumn, hasRealForeignKeyTarget, resolveActiveLooseRel } from './looseFkResolver';
import { findEnumDeclarationForPath } from './pathEnumLookup';

/** The resolved column a reopened filter compares (the VQE's `ResolvedFilterColumn` fits). */
export type ReopenedColumn = {
  readonly column: TableExplorerColumn;
  /** Absent when the field resolves to no table (then no MetaInfo applies). */
  readonly jsonContext?: JsonOperatorContext;
};

/** The equality-shaped JSON sub-operators and what a declared enum / loose FK on the path makes them. */
const JSON_PICKS: Partial<Record<JsonSubOperator, { readonly enumSub: JsonSubOperator; readonly lookupSub: JsonSubOperator }>> = {
  path_eq: { enumSub: 'property_enum', lookupSub: 'property_lookup' },
  any_eq: { enumSub: 'any_enum', lookupSub: 'any_lookup' },
  all_eq: { enumSub: 'all_enum', lookupSub: 'all_lookup' },
};

function candidates(condition: FilterCondition, { column, jsonContext }: ReopenedColumn): FilterCondition[] {
  const { operator, value } = condition;
  const out: FilterCondition[] = [];
  if ((operator === '=' || operator === '!=') && (value === 1 || value === 0)) {
    if (categorizeDataType(column.dataType) === 'boolean') out.push({ ...condition, value: value === 1 });
  }
  const looseRels = jsonContext
    ? findLooseRelationshipsForColumn(jsonContext.metaInfo?.looseRelationships, jsonContext.schema, jsonContext.table, column.name)
    : [];
  if (operator === '=' || operator === 'array_contains' || operator === 'IN') {
    const defaultOperator = defaultDraftConditionFields(column, jsonContext).operator;
    if (defaultOperator === 'enum' && (operator === '=' || operator === 'IN')) out.push({ operator: 'enum', value });
    if (operator !== 'IN') {
      if (defaultOperator === 'lookup' && hasRealForeignKeyTarget(column)) out.push({ operator: 'lookup', value });
      if (looseRels.filter((r) => !r.sourcePath).length === 1) out.push({ operator: 'lookup', value });
    }
  }
  const pick = operator === 'json' && condition.jsonSubOperator ? JSON_PICKS[condition.jsonSubOperator] : undefined;
  if (pick && condition.path && jsonContext) {
    const { schema, table, metaInfo } = jsonContext;
    if (findEnumDeclarationForPath(metaInfo?.enumColumns, schema, table, column.name, condition.path)) {
      out.push({ ...condition, jsonSubOperator: pick.enumSub });
    }
    if (resolveActiveLooseRel(looseRels, condition.path)) {
      // A scalar path: the top-level lookup through the FK's `sourcePath`
      // first (what the lookup operator writes); on PostgreSQL that one skips
      // the `::jsonb` cast, so the JSON `property_lookup` pick is the match.
      if (condition.jsonSubOperator === 'path_eq') out.push({ operator: 'lookup', value, sourcePath: condition.path });
      out.push({ ...condition, jsonSubOperator: pick.lookupSub });
    }
  }
  return out;
}

/**
 * The condition Table Explorer's chip would show for `condition` on `target`,
 * when it writes the same SQL for `field`; else `condition` itself.
 */
export function upgradeReopenedCondition(
  field: string,
  condition: FilterCondition,
  target: ReopenedColumn,
  engine: DatabaseEngineId,
): FilterCondition {
  const emit = (c: FilterCondition): string | null => {
    try {
      return buildConditionSql(field, c, engine, { valueDataType: target.column.dataType });
    } catch {
      return null;
    }
  };
  const written = emit(condition);
  if (written === null) return condition;
  return candidates(condition, target).find((next) => emit(next) === written) ?? condition;
}
